import React from 'react';
import { InfinityIcon, StarIcon } from 'lucide-react';
import { trackCustom } from '../utils/tracking';
interface OverlayBox {
  left: string;
  top: string;
  width: string;
  height: string;
}

/**
 * Turns a call-to-action that is baked into the supplied artwork into a real
 * link that scrolls to the offer section. Percentage boxes track the artwork at
 * every screen size, so these stay tappable on phones too.
 */
export function ShopHotspot({
  box
}: {
  box: OverlayBox;
}) {
  return <a href="#offer" aria-label="Shop the GritVue X1. Go to pricing" className="absolute rounded-sm transition-shadow duration-150 ease-grit hover:ring-2 hover:ring-offwhite focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" style={box} data-id="element-64" data-mp="3l7a3oka7srsr" />;
}

/**
 * Turns a navigation label baked into the supplied artwork into a real in-page link.
 */
export function NavHotspot({
  box,
  href,
  label
}: {
  box: OverlayBox;
  href: string;
  label: string;
}) {
  return <a href={href} aria-label={label} className="absolute rounded-sm transition-shadow duration-150 ease-grit hover:ring-2 hover:ring-grit-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" style={box} data-id="element-65" data-mp="3v0m43zmg9wce" />;
}

/**
 * Covers an inactive secondary button baked into the supplied artwork ("See how
 * it works", "Watch it work", "See it in action"). On wide screens the space is
 * reused for the lifetime guarantee; on phones the artwork is too small for
 * legible type, so the button is simply blacked out and the guarantee is shown
 * full width below the image via GuaranteeStrip.
 */
export function GuaranteeBadge({
  box,
  compact = false
}: {
  box: OverlayBox;
  compact?: boolean;
}) {
  return <div className="absolute flex items-center justify-center gap-[0.4vw] bg-ink-950 lg:border lg:border-grit-500 lg:px-[0.5vw]" style={box} data-id="element-66" data-mp="3h7wgxrv6qldp">
      <InfinityIcon className={`hidden shrink-0 text-grit-500 lg:block ${compact ? 'h-[0.9vw] w-[0.9vw]' : 'h-[1.3vw] w-[1.3vw]'}`} aria-hidden="true" data-id="element-67" data-mp="2olbkiczurj3a" />
      <p className="hidden min-w-0 lg:block" data-id="element-68" data-mp="xgrx77v486mz">
        <span className={`block font-display font-extrabold uppercase leading-none text-grit-500 ${compact ? 'text-[0.62vw]' : 'text-[0.9vw]'}`} data-id="element-69" data-mp="xfgulbdep4xg">
          Lifetime replacement
        </span>
        <span className={`mt-[0.1vw] block font-semibold leading-tight text-offwhite ${compact ? 'text-[0.4vw]' : 'text-[0.58vw]'}`} data-id="element-70" data-mp="16b6eth34bt99">
          On every multi-pack
        </span>
      </p>
    </div>;
}

/**
 * Blanks out a dead label baked into the artwork (the banner's old nav words) so
 * nothing on the image looks tappable when it isn't. Matches the header's black.
 */
export function ArtworkPatch({
  box
}: {
  box: OverlayBox;
}) {
  return <div aria-hidden="true" className="absolute bg-ink-950" style={box} data-id="element-71" data-mp="1tyei5tgzks8n" />;
}

/**
 * Gold rating bar under the hero banner. Tapping it scrolls to the reviews.
 */
export function ReviewsStrip({
  className = ''
}: {
  className?: string;
}) {
  return <a href="#reviews" onClick={event => {
    const target = document.getElementById('reviews');
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
    trackCustom('reviews_strip_clicked');
  }} aria-label="Rated 4.9 out of 5. See the reviews" className={`group mx-auto flex w-fit items-center justify-center gap-1.5 px-3 py-1.5 transition-opacity duration-150 ease-grit hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f2c94c] ${className}`} data-id="element-72" data-mp="2422zp49hhxih">
      <span className="flex items-center gap-px" aria-hidden="true" data-id="element-73" data-mp="1cbotlu7ppphd">
        {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className="h-3.5 w-3.5 fill-[#f2c94c] text-[#f2c94c]" data-id="element-74" data-mp="3arjizujqu03w" />)}
      </span>
      <span className="text-[13px] font-bold leading-none text-[#f2c94c]" data-id="element-75" data-mp="36p6ibkjussdz">4.9/5</span>
    </a>;
}

/**
 * The mobile counterpart to the guarantee badge: a full-width strip that reads
 * clearly on a phone instead of scaling with the artwork.
 */
export function GuaranteeStrip({
  className = ''
}: {
  className?: string;
}) {
  return <div className={`flex items-center justify-center gap-3 border-y-2 gold-edge bg-[#1a1408] px-4 py-3 text-center lg:hidden ${className}`} data-id="element-76" data-mp="34rombdhpj68x">
      <InfinityIcon className="h-6 w-6 shrink-0 text-[#f2c94c]" aria-hidden="true" data-id="element-77" data-mp="2v1mwag5sndrd" />
      <p className="text-left" data-id="element-78" data-mp="2zslg3ppzqtzd">
        <span className="block font-display text-lg font-extrabold uppercase leading-none gold-text" data-id="element-79" data-mp="2ztws2ctlb81k">
          Lifetime replacement on multi-packs
        </span>
        <span className="mt-0.5 block text-[13px] font-medium text-steel-200" data-id="element-80" data-mp="2q2sgln34i5bh">
          Break it. We replace it. Terms apply.
        </span>
      </p>
    </div>;
}